import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Container, Paper, Typography, Tabs, Tab, Button,
  CircularProgress, IconButton, Menu, MenuItem, TextField, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import * as Diff from 'diff';
import { useAuthContext } from '../context/AuthContext';
import { usePersistentNavigate } from '../utils/navigation';
import { getCaseUntilGenerated } from '../queries/getCaseUntilGenerated';
import { finalizeReview } from '../queries/finalizereview';
import { modifySection } from '../queries/modifysection';
import { syncDownstreamSections } from '../queries/syncdownstreamsections';
import { ModifySectionRequest } from '../modifysection';
import { SyncDownstreamSectionsRequest } from '../syncdownstreamsections';
import { CaseStatus, SectionType, Section } from '../schema';

// Sections map to tabs
type SectionKey = 'recommendation' | 'alternatives' | 'considerations';

interface SectionData {
  title: string;
  content: string;
  baseContent: string;
  isApproved: boolean;
  history: string[];
  modifications: string[];
}

const ReviewCase: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('case_id');
  const { accessToken, logout, setAccessToken } = useAuthContext();

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Data state
  const [loading, setLoading] = useState(true);
  const [caseSummary, setCaseSummary] = useState<string>('');
  const [sections, setSections] = useState<Record<SectionKey, SectionData>>({
    recommendation: { title: 'Recommendation', content: '', baseContent: '', isApproved: false, history: [], modifications: [] },
    alternatives: { title: 'Alternatives', content: '', baseContent: '', isApproved: false, history: [], modifications: [] },
    considerations: { title: 'Additional Considerations', content: '', baseContent: '', isApproved: false, history: [], modifications: [] },
  });

  // UI state
  const [activeTab, setActiveTab] = useState<SectionKey>('recommendation');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper component to render diff with markdown support
  const DiffViewer: React.FC<{ oldStr: string, newStr: string }> = ({ oldStr, newStr }) => {
    // Normalize line endings and trim trailing whitespace from lines to minimize noise
    const normalize = (s: string) => s.replace(/\r\n/g, '\n').split('\n').map(l => l.trimRight()).join('\n').trim();
    const cleanOld = normalize(oldStr);
    const cleanNew = normalize(newStr);

    const diff = Diff.diffWords(cleanOld, cleanNew);

    // Construct a markdown string with <ins> tags for highlighting
    let markdownWithHighlights = '';
    diff.forEach(part => {
      if (part.added) {
        // Wrap new content in <ins> tags
        // We avoid wrapping trailing newlines to prevent block-level issues
        const val = part.value;
        if (val.trim() === '') {
          markdownWithHighlights += val;
        } else {
          // If the addition contains newlines, we should wrap segments to avoid breaking block boundaries
          const segments = val.split(/(\n+)/);
          segments.forEach(seg => {
            if (seg.match(/\n+/)) {
              markdownWithHighlights += seg;
            } else if (seg.length > 0) {
              markdownWithHighlights += `<ins>${seg}</ins>`;
            }
          });
        }
      } else if (!part.removed) {
        // Keep unchanged content
        markdownWithHighlights += part.value;
      }
      // removed content is skipped
    });

    // Bubble up markdown markers (headers and list items) out of <ins> tags
    // This ensures ReactMarkdown recognizes them correctly at the start of lines.
    // We allow \s* prefix to capture indentation and newlines trapped inside <ins>
    // e.g. <ins>  * Item</ins> ->   * <ins>Item</ins>
    const processedMarkdown = markdownWithHighlights
      .replace(/<ins>(\s*#+\s+)/g, '$1<ins>')
      .replace(/<ins>(\s*[*-]\s+)/g, '$1<ins>')
      .replace(/<ins>(\s*\d+\.\s+)/g, '$1<ins>');

    return (
      <Box sx={{
        '& ins': {
          bgcolor: '#e6ffec',
          textDecoration: 'none',
          color: '#24292e',
          borderBottom: '2px solid #acf2bd',
          display: 'inline',
        },
        '& p, & li, & h1, & h2, & h3, & h4, & h5, & h6': {
          '& ins': { display: 'inline' }
        }
      }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        >
          {processedMarkdown}
        </ReactMarkdown>
      </Box>
    );
  };

  // Helper to parse markdown into sections using specified boundaries
  const parseMarkdown = (markdown: string) => {
    let summary = '';
    let recommendation = '';
    let alternatives = '';
    let considerations = '';

    // Helper to extract text between two markers
    const extract = (startMarker: string, endMarker: string | null) => {
      const startIndex = markdown.indexOf(startMarker);
      if (startIndex === -1) return '';

      if (endMarker) {
        const endIndex = markdown.indexOf(endMarker, startIndex);
        if (endIndex === -1) return markdown.substring(startIndex); // If end marker not found, go to end of file (or handle differently)
        return markdown.substring(startIndex, endIndex);
      } else {
        return markdown.substring(startIndex);
      }
    };

    summary = extract('## Summary', '## Relevant Background');
    recommendation = extract('## Primary Recommendation', '## Secondary/Alternative Recommendations');
    alternatives = extract('## Secondary/Alternative Recommendations', '## Additional Important Considerations');
    considerations = extract('## Additional Important Considerations', '## Conclusion');

    // Fallback if markers are missing (e.g. naive fallback or ensure we have distinct headers)
    // If the exact headers differ (e.g. single hash), this might fail. We will stick to the requested "## ".

    return { summary, recommendation, alternatives, considerations };
  };

  useEffect(() => {
    if (!caseId || !accessToken) return;

    const fetchCase = async () => {
      try {
        const c = await getCaseUntilGenerated(caseId, accessToken, setAccessToken, logout);

        let markdown = c.markdown || '';
        if (c.recommendations && c.recommendations.length > 0) {
          const detailed = c.recommendations.find((r: any) => r.reportType === 'detailed');
          markdown = detailed ? detailed.markdown : c.recommendations[0].markdown;
        }

        const parsed = parseMarkdown(markdown);

        setCaseSummary(parsed.summary);
        setSections(prev => ({
          recommendation: { ...prev.recommendation, content: parsed.recommendation, baseContent: parsed.recommendation, history: [] },
          alternatives: { ...prev.alternatives, content: parsed.alternatives, baseContent: parsed.alternatives, history: [] },
          considerations: { ...prev.considerations, content: parsed.considerations, baseContent: parsed.considerations, history: [] },
        }));

      } catch (error) {
        console.error("Error fetching case:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCase();
  }, [caseId, accessToken, setAccessToken, logout]);

  // Cleanup: wipe case_id parameter when unmounting
  useEffect(() => {
    return () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has('case_id')) {
        params.delete('case_id');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
        window.history.replaceState(null, '', newUrl);
      }
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: SectionKey) => {
    setActiveTab(newValue);
    setIsEditing(false); // Cancel edit if switching tabs
  };

  const handleModify = () => {
    setEditContent(''); // Clear for new notes
    setIsEditing(true);
  };

  const handleRegenerate = async () => {
    if (!caseId || !accessToken) return;

    setIsEditing(false); // Hide input immediately
    setIsRegenerating(true); // Show spinner in content area

    try {
      const currentSectionKey = activeTab;
      const currentSectionData = sections[currentSectionKey];

      // Map SectionKey to SectionType
      let sectionType = SectionType.RECOMMENDATIONS;
      if (currentSectionKey === 'alternatives') sectionType = SectionType.ALTERNATIVES;
      if (currentSectionKey === 'considerations') sectionType = SectionType.CONSIDERATIONS;

      // Construct the modify request
      const modifyRequest: ModifySectionRequest = {
        currentState: {
          sectionType: sectionType,
          title: currentSectionData.title,
          content: currentSectionData.content,
          humanFeedback: [...currentSectionData.modifications, editContent],
          originalContent: currentSectionData.baseContent
        },
        previousSections: (Object.entries(sections) as [SectionKey, SectionData][]).map(([key, data]) => {
          let sType = SectionType.RECOMMENDATIONS;
          if (key === 'alternatives') sType = SectionType.ALTERNATIVES;
          if (key === 'considerations') sType = SectionType.CONSIDERATIONS;

          return {
            sectionType: sType,
            title: data.title,
            content: data.content,
            humanFeedback: data.modifications,
            originalContent: data.baseContent
          };
        }),
        summary: caseSummary
      };

      // If modifying Recommendations, also sync Alternatives in parallel
      if (currentSectionKey === 'recommendation') {
        const syncRequest: SyncDownstreamSectionsRequest = {
          modifiedSection: {
            sectionType: SectionType.RECOMMENDATIONS,
            title: currentSectionData.title,
            content: currentSectionData.content,
            humanFeedback: [],
          },
          modificationRequest: editContent,
          downstreamSections: [{
            sectionType: SectionType.ALTERNATIVES,
            title: sections.alternatives.title,
            content: sections.alternatives.content,
            humanFeedback: [],
          }],
          summary: caseSummary
        };

        // Fire both calls in parallel
        const [modifyResult, syncResult] = await Promise.allSettled([
          modifySection(modifyRequest, accessToken, setAccessToken, logout),
          syncDownstreamSections(syncRequest, accessToken, setAccessToken, logout)
        ]);

        // Check if Recommendations modify failed - abort all
        if (modifyResult.status === 'rejected') {
          throw modifyResult.reason;
        }

        const modifyResponse = modifyResult.value;

        // Apply Recommendations update
        if (modifyResponse.updatedSection && modifyResponse.updatedSection.content) {
          setSections(prev => {
            const currentSection = prev[activeTab];
            const newHistory = [...currentSection.history, currentSection.content];
            const newModifications = [...currentSection.modifications, editContent];

            let newState = {
              ...prev,
              [activeTab]: {
                ...currentSection,
                content: modifyResponse.updatedSection!.content!,
                history: newHistory,
                modifications: newModifications
              }
            };

            // If sync succeeded, also update Alternatives
            if (syncResult.status === 'fulfilled') {
              const syncResponse = syncResult.value;
              if (syncResponse.updatedSections && syncResponse.updatedSections.length > 0) {
                const updatedAlternatives = syncResponse.updatedSections.find(
                  s => s.sectionType === SectionType.ALTERNATIVES
                );
                if (updatedAlternatives && updatedAlternatives.content) {
                  newState = {
                    ...newState,
                    alternatives: {
                      ...newState.alternatives,
                      content: updatedAlternatives.content
                    }
                  };
                }
              }
            } else {
              // Sync failed but we still apply Recommendations - log warning
              console.warn('Sync downstream sections failed, but Recommendations update applied:', syncResult.reason);
            }

            return newState;
          });
          setShowDiff(true);
        }
      } else {
        // Not modifying Recommendations - just do the regular modify
        const response = await modifySection(modifyRequest, accessToken, setAccessToken, logout);

        if (response.updatedSection && response.updatedSection.content) {
          setSections(prev => {
            const currentSection = prev[activeTab];
            const newHistory = [...currentSection.history, currentSection.content];
            const newModifications = [...currentSection.modifications, editContent];

            return {
              ...prev,
              [activeTab]: {
                ...currentSection,
                content: response.updatedSection!.content!,
                history: newHistory,
                modifications: newModifications
              }
            };
          });
          setShowDiff(true);
        }
      }
    } catch (error) {
      console.error("Failed to regenerate section:", error);
      // Ideally show an error toast here
    } finally {
      setIsRegenerating(false);
      setEditContent('');
    }
  };

  const handleRevert = () => {
    setSections(prev => {
      const currentSection = prev[activeTab];
      if (currentSection.history.length === 0) return prev;

      const newHistory = [...currentSection.history];
      const previousContent = newHistory.pop() || ''; // Remove last item
      const newModifications = [...currentSection.modifications];
      newModifications.pop();

      return {
        ...prev,
        [activeTab]: {
          ...currentSection,
          content: previousContent,
          history: newHistory,
          modifications: newModifications
        }
      };
    });
  };

  const handleApprove = () => {
    setSections(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], isApproved: true }
    }));

    // Auto-advance
    if (activeTab === 'recommendation') {
      setActiveTab('alternatives');
    } else if (activeTab === 'alternatives') {
      setActiveTab('considerations');
    }

    setIsEditing(false);
  };

  const handleFinalizeClick = () => {
    if (allApproved) {
      setShowFinalizeConfirm(true);
    }
  };

  const handleFinalizeConfirm = async () => {
    if (!caseId || !accessToken) return;
    setIsSubmitting(true);
    try {
      // Build the reviewed sections array
      const reviewedSections: Section[] = (Object.entries(sections) as [SectionKey, SectionData][]).map(([key, data]) => {
        let sectionType = SectionType.RECOMMENDATIONS;
        if (key === 'alternatives') sectionType = SectionType.ALTERNATIVES;
        if (key === 'considerations') sectionType = SectionType.CONSIDERATIONS;

        return {
          sectionType,
          title: data.title,
          content: data.content,
          humanFeedback: data.modifications,
          originalContent: data.baseContent
        };
      });

      await finalizeReview(caseId, reviewedSections, accessToken, setAccessToken, logout);
      navigate('/reviewer');
    } catch (e) {
      console.error("Error finalizing review:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allApproved = Object.values(sections).every(s => s.isApproved);

  // Icons for tabs
  const getTabIcon = (key: SectionKey) => {
    return sections[key].isApproved
      ? <CheckCircleIcon color="success" />
      : <RadioButtonUncheckedIcon color="disabled" />;
  };

  // Determine disabled state for tabs
  const isAlternativesDisabled = !sections.recommendation.isApproved;
  const isConsiderationsDisabled = !sections.alternatives.isApproved;

  // Placeholder text map
  const getPlaceholderText = () => {
    switch (activeTab) {
      case 'alternatives':
        return "Add feedback on the alternatives presented and PrecisCa AI will regenerate the section";
      case 'considerations':
        return "Add feedback on the additional considerations presented and PrecisCa AI will regenerate the section";
      default:
        return "Add notes here for what you would change about the primary recommendation and PrecisCa AI will re-generate the section";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box component="a" href="/" sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
            <Box component="img" src="/logo.jpg" alt="Precisca" sx={{ height: { xs: 28, sm: 36 }, width: 'auto', mr: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} color="inherit" sx={{ color: '#2c5282' }}>
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={() => setMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => navigate('/')}>Home</MenuItem>
              <MenuItem onClick={async () => { await logout(); }}>Log Out</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Summary Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{caseSummary}</ReactMarkdown>
          </Box>
        </Paper>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ bgcolor: 'white', borderRadius: 1, mb: 2 }}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            icon={getTabIcon('recommendation')}
            iconPosition="start"
            label="Recommendation"
            value="recommendation"
          />
          <Tab
            icon={getTabIcon('alternatives')}
            iconPosition="start"
            label="Alternatives"
            value="alternatives"
            disabled={isAlternativesDisabled}
          />
          <Tab
            icon={getTabIcon('considerations')}
            iconPosition="start"
            label="Additional Considerations"
            value="considerations"
            disabled={isConsiderationsDisabled}
          />
        </Tabs>

        {/* Content Area */}
        <Paper sx={{ p: 3, minHeight: 400 }}>
          {/* Main Content: Show Spinner OR Markdown */}
          <Box sx={{ mb: 3, minHeight: 200 }}>
            {isRegenerating ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                <CircularProgress size={40} thickness={4} />
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>Thinking...</Typography>
              </Box>
            ) : (
              showDiff && sections[activeTab].history.length > 0 ? (
                <DiffViewer
                  oldStr={sections[activeTab].history[sections[activeTab].history.length - 1]}
                  newStr={sections[activeTab].content}
                />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {sections[activeTab].content || '*No content available for this section.*'}
                </ReactMarkdown>
              )
            )}

            {/* Modification History Display */}
            {!isRegenerating && sections[activeTab].modifications.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  (Version based on feedback: {sections[activeTab].modifications.map((mod, idx) => (
                    <React.Fragment key={idx}>
                      "{mod}"{idx < sections[activeTab].modifications.length - 1 ? ' -> ' : ''}
                    </React.Fragment>
                  ))})
                </Typography>
              </Box>
            )}
          </Box>

          {!isRegenerating && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Edit / Actions Area */}
              {isEditing ? (
                <Box sx={{ mt: 2, bgcolor: '#f8f9fa', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Request Changes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={getPlaceholderText()}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2, bgcolor: 'white' }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleRegenerate}>
                      Re-generate
                    </Button>
                    <Button variant="outlined" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box>
                    {sections[activeTab].history.length > 0 && (
                      <Button
                        variant="contained"
                        onClick={() => setShowDiff(!showDiff)}
                        sx={{
                          textTransform: 'none',
                          minWidth: '120px',
                          height: '40px',
                          bgcolor: showDiff ? '#cbd5e0' : '#edf2f7',
                          color: '#2d3748',
                          '&:hover': {
                            bgcolor: showDiff ? '#a0aec0' : '#e2e8f0',
                          },
                          boxShadow: 'none',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        {showDiff ? "Hide Changes" : "Show Changes"}
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {sections[activeTab].history.length > 0 && !sections[activeTab].isApproved && (
                      <Button
                        variant="text"
                        onClick={handleRevert}
                        sx={{
                          color: '#c53030', // Dark red
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: 'rgba(197, 48, 48, 0.04)'
                          }
                        }}
                      >
                        Revert Last Change
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      onClick={handleModify}
                      disabled={sections[activeTab].isApproved}
                      sx={{
                        height: '40px',
                        minWidth: '100px',
                        textTransform: 'none'
                      }}
                    >
                      Modify
                    </Button>
                    <Button
                      variant="contained"
                      color={sections[activeTab].isApproved ? "success" : "primary"}
                      onClick={handleApprove}
                      startIcon={sections[activeTab].isApproved ? <CheckCircleIcon /> : undefined}
                      disabled={isRegenerating}
                      sx={{
                        height: '40px',
                        minWidth: '100px',
                        textTransform: 'none'
                      }}
                    >
                      {sections[activeTab].isApproved ? "Approved" : "Approve"}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Finalize Action */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            disabled={!allApproved}
            onClick={handleFinalizeClick}
            sx={{
              px: 4,
              py: 1.5,
              bgcolor: allApproved ? '#2c5282' : undefined,
              textTransform: 'none'
            }}
          >
            Finalize Review
          </Button>
        </Box>

        {/* Finalize Confirmation Dialog */}
        <Dialog open={showFinalizeConfirm} onClose={() => setShowFinalizeConfirm(false)}>
          <DialogTitle>Finalize Review?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Are you sure you want to finalize this review? This action cannot be undone and the case will be moved to your "Reviewed Cases" list.
            </DialogContentText>
            <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
              {(Object.keys(sections) as SectionKey[]).map((key) => (
                <Box key={key} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4a5568' }}>
                    {sections[key].title} Modifications:
                  </Typography>
                  {sections[key].modifications.length > 0 ? (
                    <Box component="ol" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                      {sections[key].modifications.map((mod, idx) => (
                        <Typography component="li" key={idx} variant="body2" sx={{ color: '#2d3748' }}>
                          {mod}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#718096', fontStyle: 'italic', mt: 0.5 }}>
                      None, accepted as generated
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ pb: 5, px: 3, alignItems: 'center' }}>
            <Button
              onClick={() => setShowFinalizeConfirm(false)}
              sx={{
                textTransform: 'none',
                height: '42px' // match height of contained button
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFinalizeConfirm}
              autoFocus
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                textTransform: 'none',
                height: '42px'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Final Review'}
            </Button>
          </DialogActions>
          {isSubmitting && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pb: 2, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Generating final report, this may take a minute...
              </Typography>
            </Box>
          )}
        </Dialog>

      </Container>
    </Box >
  );
};

export default ReviewCase;
